import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import { useRef, useState } from "react";
import api from "../../actprep-ui/utils/api";
import { apiUrls } from "../../actprep-ui/utils/urls";
import FormLabel from "@mui/material/FormLabel";
import ModelAutoComplete from "../../actprep-ui/components/ModelAutoComplete";
import { capitalize } from "../../actprep-ui/utils/formatting";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Switch from "@mui/material/Switch";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { actSubjects } from "../../actprep-ui/constants/program";
import { showSuccessSnackbar } from "../../actprep-ui/utils/feedback";

export default function LessonInfoForm({ lesson, onEdit, onCreate }) {
  const initialLesson = useRef(lesson);
  const [isChanged, setIsChanged] = useState(false);
  const isCreateMode = !lesson?.uuid;

  const getLessonField = (lesson, field) => {
    if (field == "category") {
      return lesson[field]
        ? {
            uuid: lesson[field].uuid,
            label: `${capitalize(lesson[field].subject)}: ${
              lesson[field].name
            }`,
          }
        : null;
    }

    if (lesson[field] == undefined || lesson[field] == null) {
      return "";
    }
    return lesson[field];
  };

  const getLessonFormData = (lesson = {}) => {
    const defaultFields = {
      name: "",
      subject: "",
      category: null,
      embed_link: "",
      link: "",
      can_embed: false,
      time: "",
      type: "",
    };

    return {
      ...defaultFields,
      ...Object.fromEntries(
        Object.keys(lesson).map((field) => [
          field,
          getLessonField(lesson, field),
        ])
      ),
    };
  };

  const [formData, setFormData] = useState(() => {
    const data = getLessonFormData(lesson);
    return data;
  });

  const handleFormChange = (field) => (event, value) => {
    if (!isChanged) {
      setIsChanged(true);
    }
    let newValue = event?.target?.value ?? value;

    if (field === "category") {
      newValue = value;
    }

    setFormData((formData) => ({
      ...formData,
      [field]: newValue,
    }));
  };

  const handleReset = () => {
    setFormData(getLessonFormData(initialLesson.current));
    setIsChanged(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const updatedFormData = {
      ...formData,
    };
    const categoryUUID = updatedFormData.category.uuid;
    updatedFormData.category_id = categoryUUID;
    delete updatedFormData.category;

    if (isCreateMode) {
      const response = await api.post(apiUrls.admin.lessons(), updatedFormData);
      showSuccessSnackbar("Lesson created successfully!");
      onCreate(response.data.data);
    } else {
      const response = await api.put(
        apiUrls.admin.lessons(lesson.uuid),
        updatedFormData
      );
      showSuccessSnackbar("Lesson updated successfully!");
      onEdit(response.data.data);
    }
  };

  return (
    <Box component={"form"} onSubmit={handleSubmit}>
      <Grid container spacing={3} sx={{ width: "100%" }}>
        <Grid size={12}>
          <Typography variant="overline" fontWeight={"bold"}>
            Lesson Info
          </Typography>
        </Grid>
        <Grid size={12}>
          <TextField
            fullWidth
            required
            value={formData.name}
            onChange={handleFormChange("name")}
            label="Name"
            variant="standard"
            type="text"
          />
        </Grid>
        <Grid size={6}>
          <FormControl fullWidth variant="standard" required>
            <InputLabel>Subject</InputLabel>
            <Select
              value={formData.subject}
              onChange={handleFormChange("subject")}
              label="Subject"
            >
              {actSubjects.map((subject) => (
                <MenuItem key={subject} value={subject}>
                  {capitalize(subject)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={6}>
          <ModelAutoComplete
            required
            modelName="Category"
            label="Category"
            value={formData.category}
            setValue={(newValue) => {
              setIsChanged(true);
              setFormData((prev) => ({ ...prev, category: newValue }));
            }}
          />
        </Grid>
        <Grid size={12}>
          <TextField
            fullWidth
            value={formData.embed_link}
            onChange={handleFormChange("embed_link")}
            label="Embed Link"
            variant="standard"
            type="url"
          />
        </Grid>
        <Grid size={12}>
          <TextField
            fullWidth
            value={formData.link}
            onChange={handleFormChange("link")}
            label="Link"
            variant="standard"
            type="text"
          />
        </Grid>
        <Grid size={6}>
          <TextField
            fullWidth
            value={formData.time}
            onChange={handleFormChange("time")}
            label="Time"
            variant="standard"
            type="text"
          />
        </Grid>
        <Grid size={6}>
          <FormLabel component="legend">
            <Typography variant="body2">Can Embed</Typography>
          </FormLabel>
          <Switch
            checked={formData.can_embed}
            color="secondary"
            onChange={(event) => {
              setIsChanged(true);
              setFormData({ ...formData, can_embed: event.target.checked });
            }}
          />
        </Grid>
        <Grid size={12}>
          <ButtonGroup fullWidth disabled={!isChanged}>
            <Button disabled={isCreateMode} onClick={handleReset}>
              Reset
            </Button>
            <Button type="submit">{isCreateMode ? "Create" : "Save"}</Button>
          </ButtonGroup>
        </Grid>
      </Grid>
    </Box>
  );
}
